/**
 * Vehicle-orientation illustrations for the accel-calibration wizard —
 * QGC-style "put the vehicle like THIS" glyphs, drawn as one quad
 * silhouette rotated per pose. `ACCELCAL_VEHICLE_POS` order:
 * 1 level · 2 left side · 3 right side · 4 nose down · 5 nose up · 6 back.
 */
export type AccelPose = 1 | 2 | 3 | 4 | 5 | 6;

export const ACCEL_POSES: { pos: AccelPose; label: string; instruction: string }[] = [
  { pos: 1, label: 'Level', instruction: 'Place the vehicle flat and level on the ground' },
  { pos: 2, label: 'Left side', instruction: 'Rest the vehicle on its LEFT side' },
  { pos: 3, label: 'Right side', instruction: 'Rest the vehicle on its RIGHT side' },
  { pos: 4, label: 'Nose down', instruction: 'Stand the vehicle on its NOSE (front pointing down)' },
  { pos: 5, label: 'Nose up', instruction: 'Stand the vehicle on its TAIL (front pointing up)' },
  { pos: 6, label: 'On its back', instruction: 'Flip the vehicle upside down' },
];

/** Whole-vehicle rotation (deg) + which profile to draw, per pose. */
const POSE_TRANSFORM: Record<AccelPose, { view: 'side' | 'front'; rotate: number; flip?: boolean }> = {
  1: { view: 'side', rotate: 0 },
  2: { view: 'front', rotate: -90 },
  3: { view: 'front', rotate: 90 },
  4: { view: 'side', rotate: -90 },
  5: { view: 'side', rotate: 90 },
  6: { view: 'side', rotate: 180 },
};

export function DroneOrientationSvg({
  pose,
  size = 132,
  active = false,
}: {
  pose: AccelPose;
  size?: number;
  active?: boolean;
}) {
  const t = POSE_TRANSFORM[pose];
  const stroke = active ? 'var(--color-accent)' : 'var(--color-fg-secondary)';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 132 132"
      fill="none"
      aria-label={ACCEL_POSES.find((p) => p.pos === pose)?.label}
    >
      {/* ground */}
      <line
        x1="14"
        y1="112"
        x2="118"
        y2="112"
        stroke="var(--color-border-default)"
        strokeWidth="2"
        strokeDasharray="5 4"
      />
      <g transform={`rotate(${t.rotate} 66 66)${t.flip ? ' scale(1,-1) translate(0,-132)' : ''}`}>
        {t.view === 'side' ? <SideProfile stroke={stroke} /> : <FrontProfile stroke={stroke} />}
      </g>
    </svg>
  );
}

/** Side view — nose (accent marker) faces RIGHT at rotate=0. */
function SideProfile({ stroke }: { stroke: string }) {
  return (
    <g strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {/* fuselage */}
      <rect x="40" y="72" width="52" height="13" rx="6.5" stroke={stroke} />
      {/* nose marker */}
      <path d="M92 74 L101 78.5 L92 83 Z" fill="var(--color-status-critical)" stroke="none" />
      {/* motor masts */}
      <line x1="50" y1="72" x2="50" y2="62" stroke={stroke} />
      <line x1="82" y1="72" x2="82" y2="62" stroke={stroke} />
      {/* props */}
      <line x1="37" y1="61" x2="63" y2="61" stroke={stroke} />
      <line x1="69" y1="61" x2="95" y2="61" stroke={stroke} />
      <ellipse cx="50" cy="61" rx="3" ry="2.2" fill={stroke} stroke="none" />
      <ellipse cx="82" cy="61" rx="3" ry="2.2" fill={stroke} stroke="none" />
      {/* landing gear */}
      <path d="M50 85 L46 97 M82 85 L86 97 M42 97 L54 97 M78 97 L90 97" stroke={stroke} />
    </g>
  );
}

/** Front view — symmetric; LEFT of the vehicle is the viewer's right. */
function FrontProfile({ stroke }: { stroke: string }) {
  return (
    <g strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {/* hub */}
      <rect x="54" y="72" width="24" height="13" rx="6.5" stroke={stroke} />
      {/* nose marker faces the viewer */}
      <circle cx="66" cy="78.5" r="3" fill="var(--color-status-critical)" stroke="none" />
      {/* arms */}
      <line x1="54" y1="76" x2="30" y2="68" stroke={stroke} />
      <line x1="78" y1="76" x2="102" y2="68" stroke={stroke} />
      {/* motor masts + props */}
      <line x1="30" y1="68" x2="30" y2="60" stroke={stroke} />
      <line x1="102" y1="68" x2="102" y2="60" stroke={stroke} />
      <line x1="18" y1="59" x2="42" y2="59" stroke={stroke} />
      <line x1="90" y1="59" x2="114" y2="59" stroke={stroke} />
      <ellipse cx="30" cy="59" rx="3" ry="2.2" fill={stroke} stroke="none" />
      <ellipse cx="102" cy="59" rx="3" ry="2.2" fill={stroke} stroke="none" />
      {/* landing gear */}
      <path d="M58 85 L54 97 M74 85 L78 97 M50 97 L62 97 M70 97 L82 97" stroke={stroke} />
    </g>
  );
}
